import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Button, Group, SimpleGrid, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  FilterDrawer,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type FilterField,
  type FilterValues,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { downloadText, rowsToCsv } from '@repletoryreach/sdk-utils/file';
import { admin } from '@repletoryreach/sdk-api';
import { useUsageMargin } from '../../../data/useSkuRates';
import { usePersistedState } from '../../../lib/usePersistedState';
import { useTenantMap } from '../../../data/useTenants';
import { formatDateTime, formatInteger, formatMinor } from '../../../lib/format';
import { UsageTable, type UsageRow } from './components/UsageTable';
import { UsageList } from './components/UsageList';
import { StatCard } from '../../../components/StatCard';
import { CurrencySwitch, amountIn, useDisplayCurrency } from '../../../components/CurrencySwitch';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { BillingToolbar } from '../components/BillingToolbar';
import { ViewToggle } from '../components/ViewToggle';
import { TopList, type TopListItem } from '../components/TopList';
import { IconChart, IconCredit, IconDebit, IconDownload, IconLayers } from '../components/icons';
import { rangeSince, type DateRangeKey } from '../components/dateRange';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';

/** The server clamps `limit` here, so this is the largest sample the page can take. */
const SAMPLE_SIZE = 1000;

const STAT_COLS = { base: 1, sm: 2, lg: 4 };
const TOP_N = 10;

const TONES = ['healthy', 'pending', 'warning', 'down'] as const;

/**
 * A currency's chip keeps the same tone whatever else is on the page. Tones
 * used to be handed out by array index, so a currency changed colour whenever
 * another one appeared or dropped out of the rollup.
 */
function currencyTone(code: string): (typeof TONES)[number] {
  let hash = 0;
  for (let i = 0; i < code.length; i += 1) hash = (hash * 31 + code.charCodeAt(i)) >>> 0;
  return TONES[hash % TONES.length];
}

export default function BillingUsagePage() {
  const t = useT();
  const nav = useNavigate();
  // Named `range`, not `window`: the previous name shadowed the global inside
  // the whole component.
  const [range, setRange] = useState<DateRangeKey>('7d');

  // The range is part of the key so changing it refetches. It still only
  // re-slices a fixed sample, because `GET /v1/admin/ledger` takes no `since`.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'ledger', 'usage', range],
    queryFn: () => admin.listLedger({ limit: SAMPLE_SIZE })
  });

  const { map: tenants } = useTenantMap();

  // Revenue against cost for the same window, from the cost snapshot on every
  // ledger entry: what the platform actually earned on the usage below.
  const marginQ = useUsageMargin({ since: rangeSince(range) === null ? undefined : new Date(rangeSince(range)!).toISOString() });
  const margin = marginQ.data?.data;

  const [view, setView] = usePersistedState<ViewMode>('admin.usage.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.usage.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<string | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const source = useMemo(() => data?.data ?? [], [data]);

  /** The sample rows the chosen window admits: everything downstream reads this. */
  const inWindow = useMemo(() => {
    const since = rangeSince(range);
    if (since === null) return source;
    return source.filter((e) => {
      const at = new Date(e.occurred_at).getTime();
      return Number.isNaN(at) || at >= since;
    });
  }, [source, range]);

  /**
   * What the sample actually covers, so the operator can see when the selected
   * range is wider than the rows the endpoint returned.
   */
  const coverage = useMemo(() => {
    if (source.length === 0) return null;
    const times = source.map((e) => new Date(e.occurred_at).getTime()).filter((n) => !Number.isNaN(n));
    if (times.length === 0) return null;
    return { from: new Date(Math.min(...times)).toISOString(), to: new Date(Math.max(...times)).toISOString() };
  }, [source]);

  const sampleExhausted = source.length >= SAMPLE_SIZE;

  // Roll the ledger up per tenant *and* currency. Keyed on the tenant alone,
  // an org billed in ZAR and USD had both currencies' minor units added into
  // one total under whichever currency happened to appear first.
  const rollup = useMemo<UsageRow[]>(() => {
    const map = new Map<string, UsageRow>();
    for (const e of inWindow) {
      const id = `${e.organisation_id}:${e.currency}`;
      const cur = map.get(id) ?? {
        id,
        organisation_id: e.organisation_id,
        events: 0,
        debit_minor: 0,
        credit_minor: 0,
        currency: e.currency
      };
      cur.events += 1;
      if (e.amount_minor < 0) cur.debit_minor += -e.amount_minor;
      else cur.credit_minor += e.amount_minor;
      map.set(id, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.debit_minor - a.debit_minor);
  }, [inWindow]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;

  function clearAllFilters() {
    setQuery('');
    setKpi('all');
    setFilters({});
    setPage(1);
  }

  const currencies = useMemo(
    () => Array.from(new Set(rollup.map((r) => r.currency))).sort(),
    [rollup]
  );
  const kpiItems: KpiFilterItem[] = useMemo(() => [
    { id: 'all', label: t('billing.usage.kpi.all'), value: rollup.length, tone: 'primary' as const },
    ...currencies.map((c) => ({
      id: c,
      label: c,
      value: rollup.filter((r) => r.currency === c).length,
      tone: currencyTone(c)
    }))
  ], [rollup, currencies, t]);

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: 'currency',
        label: t('common.currency'),
        placeholder: t('billing.ledger.filters.anyCurrency'),
        options: currencies.map((c) => ({ value: c, label: c }))
      },
      {
        key: 'activity',
        label: t('billing.usage.filters.activity'),
        placeholder: t('billing.usage.filters.anyActivity'),
        options: [
          { value: 'has_debit', label: t('billing.usage.filters.hasDebits') },
          { value: 'credit_only', label: t('billing.usage.filters.creditsOnly') }
        ]
      }
    ],
    [currencies, t]
  );

  const rangeLabels: Record<DateRangeKey, string> = {
    all: t('billing.range.all'), today: t('billing.range.today'), '7d': t('billing.range.last7'), '30d': t('billing.range.last30')
  };

  const pills: ActiveFilterPill[] = [];
  if (range !== 'all') pills.push({ id: 'range', field: t('billing.range.label'), value: rangeLabels[range] });
  if (kpi && kpi !== 'all') pills.push({ id: 'kpi', field: t('common.currency'), value: kpi });
  if (filters.currency) pills.push({ id: 'currency', field: t('common.currency'), value: filters.currency });
  if (filters.activity) {
    pills.push({
      id: 'activity',
      field: t('billing.usage.filters.activity'),
      value: filters.activity === 'has_debit' ? t('billing.usage.filters.hasDebits') : t('billing.usage.filters.creditsOnly')
    });
  }
  function removePill(id: string) {
    setPage(1);
    if (id === 'range') { setRange('all'); return; }
    if (id === 'kpi') { setKpi('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let rows = rollup;
    if (kpi && kpi !== 'all') rows = rows.filter((r) => r.currency === kpi);
    if (filters.currency) rows = rows.filter((r) => r.currency === filters.currency);
    if (filters.activity === 'has_debit') rows = rows.filter((r) => r.debit_minor > 0);
    if (filters.activity === 'credit_only') rows = rows.filter((r) => r.debit_minor === 0);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q) ||
          (tenants.get(r.organisation_id)?.slug ?? '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [rollup, kpi, filters, query, tenants]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  /** Totals per currency, for the strip under the table and the stat cards. */
  const totals = useMemo(() => {
    const map = new Map<string, { events: number; debit: number; credit: number }>();
    for (const r of filtered) {
      const cur = map.get(r.currency) ?? { events: 0, debit: 0, credit: 0 };
      cur.events += r.events;
      cur.debit += r.debit_minor;
      cur.credit += r.credit_minor;
      map.set(r.currency, cur);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // One currency at a time on the cards: three joined figures overflowed the
  // card, and a sum across currencies would be a number that means nothing.
  // Busiest currency first, so a first visit lands on the one with the most entries.
  const currenciesInWindow = useMemo(() => [...totals].sort(([, a], [, b]) => b.events - a.events).map(([currency]) => currency), [totals]);
  const [displayCurrency, setDisplayCurrency] = useDisplayCurrency(currenciesInWindow);
  // A currency chip narrows the table to that currency; the cards follow it.
  useEffect(() => {
    if (kpi && kpi !== 'all' && currenciesInWindow.includes(kpi)) setDisplayCurrency(kpi);
  }, [kpi, currenciesInWindow, setDisplayCurrency]);
  const debitByCurrency = new Map(totals.map(([currency, v]) => [currency, v.debit]));
  const creditByCurrency = new Map(totals.map(([currency, v]) => [currency, v.credit]));
  const debitTotal = amountIn(debitByCurrency, displayCurrency);
  const creditTotal = amountIn(creditByCurrency, displayCurrency);
  const otherCurrencies = currenciesInWindow.filter((c) => c !== displayCurrency);
  const othersHint = otherCurrencies.length > 0 ? t('billing.currency.othersHint', { list: otherCurrencies.join(', ') }) : null;
  const tenantsActive = new Set(filtered.map((r) => r.organisation_id)).size;
  const eventsCounted = filtered.reduce((sum, r) => sum + r.events, 0);

  /** The dearest tenants in the window, as a ranked bar list. */
  const topTenants: TopListItem[] = useMemo(() => {
    const rows = [...filtered].sort((a, b) => b.debit_minor - a.debit_minor).slice(0, TOP_N);
    const max = rows[0]?.debit_minor ?? 0;
    return rows.map((r) => ({
      id: r.id,
      label: tenants.get(r.organisation_id)?.name ?? t('tenantName.unknownShort'),
      value: formatMinor(r.debit_minor, r.currency),
      fraction: max > 0 ? r.debit_minor / max : 0
    }));
  }, [filtered, tenants, t]);

  /**
   * The dearest SKUs in the window. Debits are summed per SKU and currency, so
   * a SKU billed in two currencies ranks as two entries rather than as one
   * meaningless sum.
   */
  const skuRanking = useMemo(() => {
    const map = new Map<string, { sku: string; currency: string; events: number; debit: number }>();
    for (const e of inWindow) {
      if (!e.sku_code || e.amount_minor >= 0) continue;
      const id = `${e.sku_code}:${e.currency}`;
      const cur = map.get(id) ?? { sku: e.sku_code, currency: e.currency, events: 0, debit: 0 };
      cur.events += 1;
      cur.debit += -e.amount_minor;
      map.set(id, cur);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].debit - a[1].debit);
  }, [inWindow]);

  const topSkus: TopListItem[] = useMemo(() => {
    const rows = skuRanking.slice(0, TOP_N);
    const max = rows[0]?.[1].debit ?? 0;
    return rows.map(([id, v]) => ({
      id,
      label: <code>{v.sku}</code>,
      value: formatMinor(v.debit, v.currency),
      fraction: max > 0 ? v.debit / max : 0
    }));
  }, [skuRanking]);

  function exportCsv() {
    const rows = filtered.map((r) => ({
      organisation: tenants.get(r.organisation_id)?.name ?? '',
      organisation_id: r.organisation_id,
      currency: r.currency,
      events: r.events,
      debit: formatMinor(r.debit_minor, r.currency),
      debit_minor: r.debit_minor,
      credit: formatMinor(r.credit_minor, r.currency),
      credit_minor: r.credit_minor,
      net: formatMinor(r.credit_minor - r.debit_minor, r.currency),
      net_minor: r.credit_minor - r.debit_minor
    }));
    const headers = [
      'organisation', 'organisation_id', 'currency', 'events',
      'debit', 'debit_minor', 'credit', 'credit_minor', 'net', 'net_minor'
    ];
    downloadText(rowsToCsv(rows, { headers, bom: true }), `usage-${range}-${Date.now()}.csv`, 'text/csv');
    notifySuccess(
      t('billing.usage.toast.exported', { count: rows.length }),
      t('billing.usage.toast.exportedTitle')
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.usage.eyebrow')}
        title={t('billing.usage.title')}
        description={t('billing.usage.description')}
        actions={
          <Group gap="sm" wrap="nowrap">
            <Button size="sm" variant="outline" leftSection={<IconDownload />} disabled={filtered.length === 0} onClick={exportCsv}>
              {t('action.exportCsv')}
            </Button>
            <ViewToggle value={view === 'cards' ? 'table' : view} onChange={setView} />
          </Group>
        }
      />

      {sampleExhausted && (
        <Alert color="warning" title={t('billing.usage.truncated.title')}>
          {coverage
            ? t('billing.usage.truncated.covered', {
                limit: SAMPLE_SIZE,
                from: formatDateTime(coverage.from),
                to: formatDateTime(coverage.to)
              })
            : t('billing.usage.truncated.body', { limit: SAMPLE_SIZE })}
        </Alert>
      )}

      <CurrencySwitch currencies={currenciesInWindow} value={displayCurrency} onChange={setDisplayCurrency} />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard
          withRule tone="error" icon={<IconDebit />}
          label={t('billing.usage.stat.debit')}
          value={debitTotal || t('billing.subscriptions.metric.noRevenue')}
          hint={othersHint ?? t('billing.usage.stat.debitHint')}
          loading={isLoading}
        />
        <StatCard
          withRule tone="success" icon={<IconCredit />}
          label={t('billing.usage.stat.credit')}
          value={creditTotal || t('billing.subscriptions.metric.noRevenue')}
          hint={othersHint ?? t('billing.usage.stat.creditHint')}
          loading={isLoading}
        />
        <StatCard
          withRule tone="primary" icon={<IconLayers />}
          label={t('billing.usage.stat.tenants')}
          value={formatInteger(tenantsActive)}
          hint={t('billing.usage.stat.tenantsHint')}
          loading={isLoading}
        />
        <StatCard
          withRule tone="info" icon={<IconChart />}
          label={t('billing.usage.stat.events')}
          value={formatInteger(eventsCounted)}
          hint={coverage
            ? t('billing.usage.sampleCovers', { from: formatDateTime(coverage.from), to: formatDateTime(coverage.to) })
            : t('billing.usage.stat.eventsHint')}
          loading={isLoading}
        />
      </SimpleGrid>

      {/* Revenue against cost, from the cost snapshot every ledger entry
          carries, in the currency the cards above show. */}
      {(() => {
        const dc = displayCurrency ?? 'ZAR';
        const cur = margin?.by_currency.find((c) => c.currency === dc) ?? null;
        const pct = cur && cur.revenue_minor > 0 ? Math.round((cur.margin_minor * 1000) / cur.revenue_minor) / 10 : null;
        return (
          <>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <StatCard
                withRule tone="primary" icon={<IconCredit />}
                label={t('billing.usage.stat.revenue')}
                value={cur ? formatMinor(cur.revenue_minor, dc) : '–'}
                hint={t('billing.usage.stat.revenueHint')}
                loading={marginQ.isLoading}
              />
              <StatCard
                withRule tone="warning" icon={<IconDebit />}
                label={t('billing.usage.stat.cost')}
                value={cur ? formatMinor(cur.cost_minor, dc) : '–'}
                hint={t('billing.usage.stat.costHint')}
                loading={marginQ.isLoading}
              />
              <StatCard
                withRule tone={pct != null && pct < 10 ? 'error' : 'success'} icon={<IconChart />}
                label={t('billing.usage.stat.margin')}
                value={cur ? `${formatMinor(cur.margin_minor, dc)}${pct == null ? '' : ` (${pct}%)`}` : '–'}
                hint={t('billing.usage.stat.marginHint')}
                loading={marginQ.isLoading}
              />
            </SimpleGrid>
            <TopList
              title={t('billing.usage.margin.title')}
              tone="var(--rr-color-success)"
              empty={t('billing.usage.margin.empty')}
              items={(cur?.by_category ?? []).map((c) => ({
                id: c.category,
                label: t(`billing.usage.category.${c.category}`, c.category),
                value: formatMinor(c.margin_minor, dc),
                fraction: cur && cur.by_category[0]?.margin_minor ? Math.max(0, c.margin_minor / cur.by_category[0].margin_minor) : 0
              }))}
              footer={cur ? t('billing.usage.margin.row', { revenue: formatMinor(cur.revenue_minor, dc), cost: formatMinor(cur.cost_minor, dc) }) : undefined}
            />
          </>
        );
      })()}

      <KpiFilterRow
        items={kpiItems}
        value={kpi}
        onChange={(next) => { setKpi(next ?? 'all'); setPage(1); }}
      />

      <BillingToolbar
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('billing.usage.search')}
        range={range}
        onRangeChange={(next) => { setRange(next); setPage(1); }}
      >
        <FilterDrawer
          fields={filterFields}
          values={filters}
          onApply={(next) => { setFilters(next); setPage(1); }}
          onClear={clearAllFilters}
        />
      </BillingToolbar>

      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <div className="rr-billing-split">
        <TableShell
          totalLabel={t('billing.usage.showingCount', { shown: formatInteger(filtered.length), total: formatInteger(rollup.length) })}
          loading={isLoading}
          isEmpty={filtered.length === 0}
          empty={
            <EmptyState
              title={t('billing.usage.empty')}
              description={hasActiveFilters ? t('common.tryClearingFilters') : t('billing.usage.emptyHint')}
              action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
            />
          }
          error={
            error ? (
              <Alert color="error">
                <Stack gap="sm">
                  <Text role="bodySmall">{t('billing.usage.loadError')}</Text>
                  <Group>
                    <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
                  </Group>
                </Stack>
              </Alert>
            ) : null
          }
          pagination={
            <TableFooter
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              capped={sampleExhausted}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          }
        >
          {view === 'table' && (
            <UsageTable
              records={paged}
              loading={isLoading}
              onRowClick={(r) => nav(`/billing/ledger?org_id=${encodeURIComponent(r.organisation_id)}`)}
            />
          )}
          {view === 'list' && (
            <UsageList
              records={paged}
              onSelect={(r) => nav(`/billing/ledger?org_id=${encodeURIComponent(r.organisation_id)}`)}
            />
          )}
          {totals.length > 0 && (
            <div className="rr-billing-totals">
              <Text role="caption" fw={700}>{t('billing.usage.totals')}</Text>
              {totals.map(([currency, v]) => (
                <Text key={currency} role="caption" c="var(--rr-color-text-muted)">
                  {t('billing.usage.totalsRow', {
                    currency,
                    events: formatInteger(v.events),
                    debit: formatMinor(v.debit, currency),
                    credit: formatMinor(v.credit, currency),
                    net: formatMinor(v.credit - v.debit, currency)
                  })}
                </Text>
              ))}
            </div>
          )}
        </TableShell>

        <Stack gap="md">
          <TopList
            title={t('billing.usage.topTenants')}
            items={topTenants}
            empty={t('billing.usage.empty')}
          />
          <TopList
            title={t('billing.usage.topSkus')}
            items={topSkus}
            empty={t('billing.usage.noSkus')}
            footer={skuRanking.length > 0
              ? t('billing.usage.topSkusHint', { shown: formatInteger(topSkus.length), total: formatInteger(skuRanking.length) })
              : undefined}
          />
        </Stack>
      </div>
    </Stack>
  );
}
